import { useCallback, useContext } from "react";
import useReport from "./useReport.hook";
import useCatalogApi from "./useCatalogApi.hook";
import { UserContext } from "../pages/UserContext";
import canDownloadProductAcr from "../pages/Product/utils/canDownloadProductAcr";
import inSiteAcrAddedOn from "../pages/Product/utils/inSiteAcrAddedOn";
import loadProductAcrExportModel, {
  findCompletedReport,
} from "../components/acr-editor/utils/acrExport/loadProductAcrExportModel";

/**
 * Product Listing in-site ACR export support. Centralizes the "does this product have a completed
 * ACR the current user may download" gate and the "build the HTML/JSON/YAML export model" flow so
 * the listing page can show export checkboxes and bulk-download the selected ACRs. The download gate
 * mirrors the product detail page exactly (via the shared canDownloadProductAcr util) and is a UI
 * convenience only — the API still authorizes the underlying report/catalog requests.
 */
export default function useProductAcrExport() {
  const { fetchReportsByProduct } = useReport();
  const { getCompleteCatalogById, getConformanceLevels } = useCatalogApi();
  const { isSuperUser, isVendorUser, isAgencyUser, user } = useContext(UserContext);

  /** Whether the current user may download the given product's completed report. */
  const canDownload = useCallback(
    (product, report) =>
      canDownloadProductAcr({
        isSuperUser,
        isVendorUser,
        isAgencyUser,
        userVendorId: user?.vendorId,
        productVendorId: product?.vendorId,
        userAgencyId: user?.agencyId,
        productAgencyId: product?.agencyId,
        reportIsPublic: report?.isPublic,
      }),
    [isSuperUser, isVendorUser, isAgencyUser, user]
  );

  /** The product's completed report if the current user is allowed to download it, else null. */
  const resolveDownloadableReport = useCallback(
    async (product) => {
      if (!product?.id) {
        return null;
      }
      const reports = await fetchReportsByProduct(product.id);
      const report = findCompletedReport(reports);
      if (!report) {
        return null;
      }
      return canDownload(product, report) ? report : null;
    },
    [fetchReportsByProduct, canDownload]
  );

  /**
   * Resolve which of the given products have a completed ACR the current user may download.
   * Each product is queried on its own (`productId` scope). An unscoped completed-report list
   * would download every ACR in the repository, including private ones this page never shows.
   * One product's failure does not fail the rest: `failedIds` are the lookups the caller should
   * retry, and they are omitted from `availability` so a false "unavailable" is not cached.
   * `addedOn` is the in-site ACR date for downloadable products, keyed by product id.
   */
  const getAvailabilityMap = useCallback(
    async (products) => {
      const withId = (products ?? []).filter((product) => product?.id);
      if (withId.length === 0) {
        return { availability: {}, addedOn: {}, failedIds: [] };
      }

      const settled = await Promise.allSettled(withId.map(async (product) => {
        const reports = await fetchReportsByProduct(product.id);
        const report = findCompletedReport(reports);
        const downloadable = Boolean(report && canDownload(product, report));
        return {
          id: String(product.id),
          downloadable,
          addedOn: downloadable ? inSiteAcrAddedOn(report) : null,
        };
      }));

      const availability = {};
      const addedOn = {};
      const failedIds = [];
      settled.forEach((result, index) => {
        if (result.status === "rejected") {
          failedIds.push(String(withId[index].id));
          return;
        }
        availability[result.value.id] = result.value.downloadable;
        if (result.value.addedOn) {
          addedOn[result.value.id] = result.value.addedOn;
        }
      });
      return { availability, addedOn, failedIds };
    },
    [fetchReportsByProduct, canDownload]
  );

  /** Build the editor evaluation + catalog for a product's in-site ACR so it can be exported. */
  const buildExportModel = useCallback(
    async (product) => {
      try {
        const report = await resolveDownloadableReport(product);
        if (!report) {
          return { ok: false, reason: "unavailable" };
        }
        return loadProductAcrExportModel({
          product,
          vendor: product?.vendor,
          report,
          fetchReportsByProduct,
          getCompleteCatalogById,
          getConformanceLevels,
        });
      } catch {
        return { ok: false, reason: "load-failed" };
      }
    },
    [resolveDownloadableReport, fetchReportsByProduct, getCompleteCatalogById, getConformanceLevels]
  );

  return { getAvailabilityMap, buildExportModel };
}
